import React, { useEffect, useMemo, useRef, useState } from "react";
import { adminApi, absoluteAdminUrl } from "./api";
import { formatINR, parseNumber, shortDate } from "./format";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  X,
  ShoppingBag,
  ImagePlus,
  Loader2,
  PackageX,
  Tag,
  Check,
  Truck,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const EMPTY = {
  name: "",
  slug: "",
  category: "Uncategorized",
  description: "",
  price: 0,
  compare_at_price: "",
  images: [],
  stock: 0,
  is_active: true,
  shipping_fee: "",
};

const ORDER_STATUSES = ["pending", "paid", "failed", "cancelled", "shipped", "delivered"];
const STATUS_CHIP = {
  pending: "ax-chip-warn",
  paid: "ax-chip-success",
  failed: "ax-chip-danger",
  cancelled: "ax-chip-danger",
  shipped: "ax-chip-info",
  delivered: "ax-chip-gold",
};

export default function ShopModule() {
  const [tab, setTab] = useState("products");
  const [categories, setCategories] = useState([]);
  const [catLoading, setCatLoading] = useState(true);

  const refreshCategories = async () => {
    try {
      const r = await adminApi.get("/shop-categories");
      setCategories(r.data?.categories || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load categories");
    } finally {
      setCatLoading(false);
    }
  };

  useEffect(() => {
    refreshCategories();
  }, []);

  return (
    <div className="p-10 max-w-[1320px] mx-auto" data-testid="shop-module">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShoppingBag className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Shop
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Storefront</h1>
          <p className="text-sm mt-3" style={{ color: "var(--ax-text-dim)" }}>
            Manage products, categories and incoming orders — just like WooCommerce.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`ax-btn ${tab === "products" ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setTab("products")}
            data-testid="shop-tab-products"
          >
            Products
          </button>
          <button
            type="button"
            className={`ax-btn ${tab === "categories" ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setTab("categories")}
            data-testid="shop-tab-categories"
          >
            Categories
          </button>
          <button
            type="button"
            className={`ax-btn ${tab === "orders" ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setTab("orders")}
            data-testid="shop-tab-orders"
          >
            Orders
          </button>
          <button
            type="button"
            className={`ax-btn ${tab === "settings" ? "ax-btn-gold" : "ax-btn-ghost"}`}
            onClick={() => setTab("settings")}
            data-testid="shop-tab-settings"
          >
            Settings
          </button>
        </div>
      </header>
      {tab === "products" && <ShopProducts categories={categories} />}
      {tab === "categories" && (
        <ShopCategories categories={categories} loading={catLoading} onChanged={refreshCategories} />
      )}
      {tab === "orders" && <ShopOrders />}
      {tab === "settings" && <ShopSettingsTab categories={categories} />}
    </div>
  );
}

function ShopProducts({ categories }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/shop-products");
      setProducts(r.data?.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shop products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter((p) => {
      if (catFilter !== "all" && p.category !== catFilter) return false;
      if (!term) return true;
      return (p.name || "").toLowerCase().includes(term) || (p.slug || "").toLowerCase().includes(term);
    });
  }, [products, q, catFilter]);

  const onSave = async (data, existing) => {
    try {
      if (existing) {
        await adminApi.put(`/shop-products/${existing.id}`, data);
        toast.success("Product updated");
      } else {
        await adminApi.post("/shop-products", data);
        toast.success("Product added to storefront");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the shop?`)) return;
    try {
      await adminApi.delete(`/shop-products/${p.id}`);
      toast.success("Product removed");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <div className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}
        >
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="Search by name or slug"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="shop-search"
          />
        </div>
        <select className="ax-select" style={{ width: 180 }} value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="shop-filter-cat">
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
          {filtered.length} / {products.length}
        </div>
        <button type="button" className="ax-btn ax-btn-gold" onClick={() => setEditing("new")} data-testid="shop-add-product">
          <Plus className="w-4 h-4" /> Add product
        </button>
      </div>

      <div className="ax-glass overflow-hidden" data-testid="shop-table-wrap">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}></th>
                <th>Product</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Updated</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    No products match.
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((p) => (
                  <tr key={p.id} data-testid={`shop-row-${p.id}`}>
                    <td>
                      {p.images?.[0] ? (
                        <img src={absoluteAdminUrl(p.images[0])} alt="" className="w-12 h-12 rounded-md object-cover" style={{ border: "1px solid var(--ax-border)" }} />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-md flex items-center justify-center"
                          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid var(--ax-border)" }}
                        >
                          <ShoppingBag className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="text-sm font-medium">{p.name}</div>
                      <div className="text-[11px] mt-0.5" style={{ color: "var(--ax-text-muted)" }}>
                        /shop/{p.slug}
                      </div>
                    </td>
                    <td>
                      <span className="ax-chip">{p.category}</span>
                    </td>
                    <td className="text-sm ax-mono" style={{ color: "var(--ax-gold)" }}>
                      {formatINR(p.price)}
                    </td>
                    <td className="text-sm">{p.stock ?? 0}</td>
                    <td>
                      <span className={`ax-chip ${p.is_active ? "ax-chip-success" : "ax-chip-danger"}`}>
                        {p.is_active ? "Live" : "Hidden"}
                      </span>
                    </td>
                    <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
                      {shortDate(p.updated_at)}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        <button type="button" className="ax-btn ax-btn-ghost px-2" onClick={() => setEditing(p)} data-testid={`shop-edit-${p.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" className="ax-btn ax-btn-ghost ax-btn-danger px-2" onClick={() => onDelete(p)} data-testid={`shop-delete-${p.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <ProductDialog
          product={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </>
  );
}

function ProductDialog({ product, categories, onClose, onSave }) {
  const defaultCategory = categories?.[0]?.name || "Uncategorized";
  const [form, setForm] = useState(() => ({ ...EMPTY, category: defaultCategory, ...(product || {}) }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const categoryOptions = useMemo(() => {
    const names = categories.map((c) => c.name);
    if (form.category && !names.includes(form.category)) names.unshift(form.category);
    return names;
  }, [categories, form.category]);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await adminApi.post("/shop-products/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("images", [...(form.images || []), r.data?.image_url].filter(Boolean));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => {
    set("images", (form.images || []).filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: parseNumber(form.price),
      compare_at_price: form.compare_at_price ? parseNumber(form.compare_at_price) : null,
      stock: parseInt(parseNumber(form.stock), 10) || 0,
      slug: form.slug?.trim() || undefined,
      shipping_fee: form.shipping_fee !== "" && form.shipping_fee != null ? parseNumber(form.shipping_fee) : null,
    };
    await onSave(payload, product);
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 ax-scroll overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      data-testid="shop-dialog"
    >
      <form onSubmit={submit} className="ax-glass w-full max-w-3xl p-7 relative my-10" style={{ borderRadius: 18 }}>
        <button type="button" className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={onClose} data-testid="shop-dialog-close">
          <X className="w-4 h-4" />
        </button>
        <div className="mb-5">
          <div className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
            {product ? "Edit" : "New"} product
          </div>
          <h2 className="ax-serif text-3xl mt-1">{product ? product.name : "Add to storefront"}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="md:col-span-1">
            <label className="ax-label">Images</label>
            <div
              className="rounded-xl flex items-center justify-center cursor-pointer overflow-hidden relative"
              style={{ height: 140, background: "rgba(255,255,255,0.02)", border: "1px dashed var(--ax-border)" }}
              onClick={() => fileRef.current?.click()}
              data-testid="shop-image-drop"
            >
              <div className="text-center" style={{ color: "var(--ax-text-muted)" }}>
                <ImagePlus className="w-6 h-6 mx-auto mb-2" />
                <div className="text-xs">Click to add image</div>
                <div className="text-[10px] mt-1">PNG / JPG / WEBP · up to 5 MB</div>
              </div>
              {uploading && (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.6)" }}>
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--ax-gold)" }} />
                </div>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid="shop-image-input"
            />
            {form.images?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {form.images.map((img, i) => (
                  <div key={i} className="relative w-14 h-14 rounded-lg overflow-hidden" style={{ border: "1px solid var(--ax-border)" }}>
                    <img src={absoluteAdminUrl(img)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      data-testid={`shop-image-remove-${i}`}
                      className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 flex items-center justify-center"
                    >
                      <X className="w-2.5 h-2.5 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="ax-label">Product name *</label>
              <input className="ax-input" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="shop-field-name" />
            </div>
            <div>
              <label className="ax-label">Category</label>
              <select className="ax-select" value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="shop-field-category">
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="ax-label">Slug</label>
              <input className="ax-input" value={form.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="auto-generated" data-testid="shop-field-slug" />
            </div>
            <div>
              <label className="ax-label">Price (₹)</label>
              <input className="ax-input ax-mono" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="shop-field-price" />
            </div>
            <div>
              <label className="ax-label">Compare-at price (₹, optional)</label>
              <input className="ax-input ax-mono" value={form.compare_at_price || ""} onChange={(e) => set("compare_at_price", e.target.value)} data-testid="shop-field-compare-price" />
            </div>
            <div>
              <label className="ax-label">Stock</label>
              <input className="ax-input ax-mono" value={form.stock ?? 0} onChange={(e) => set("stock", e.target.value)} data-testid="shop-field-stock" />
            </div>
            <div>
              <label className="ax-label">Shipping fee override (₹, optional)</label>
              <input
                className="ax-input ax-mono"
                placeholder="Uses global/category default"
                value={form.shipping_fee ?? ""}
                onChange={(e) => set("shipping_fee", e.target.value)}
                data-testid="shop-field-shipping-fee"
              />
            </div>
            <div className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="shop-field-is-active" />
              <label className="text-sm" style={{ color: "var(--ax-text-dim)" }}>
                Visible in shop
              </label>
            </div>
            <div className="md:col-span-2">
              <label className="ax-label">Description</label>
              <textarea
                className="ax-textarea"
                rows={3}
                placeholder="What makes this product worth buying?"
                value={form.description || ""}
                onChange={(e) => set("description", e.target.value)}
                data-testid="shop-field-description"
              />
            </div>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-end gap-3">
          <button type="button" className="ax-btn ax-btn-ghost" onClick={onClose} data-testid="shop-dialog-cancel">
            Cancel
          </button>
          <button type="submit" className="ax-btn ax-btn-gold" disabled={saving} data-testid="shop-dialog-save">
            {saving ? "Saving…" : product ? "Save changes" : "Add product"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ShopCategories({ categories, loading, onChanged }) {
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const addCategory = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    try {
      await adminApi.post("/shop-categories", { name });
      toast.success(`"${name}" category added`);
      setNewName("");
      onChanged();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not add category");
    } finally {
      setAdding(false);
    }
  };

  const startRename = (cat) => {
    setEditingId(cat.id);
    setEditName(cat.name);
  };

  const saveRename = async (cat) => {
    const name = editName.trim();
    if (!name || name === cat.name) {
      setEditingId(null);
      return;
    }
    try {
      await adminApi.put(`/shop-categories/${cat.id}`, { name });
      toast.success("Category renamed");
      setEditingId(null);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Delete "${cat.name}"? Products in it will move to Uncategorized.`)) return;
    try {
      await adminApi.delete(`/shop-categories/${cat.id}`);
      toast.success("Category deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <>
      <form onSubmit={addCategory} className="ax-glass mb-5 p-4 flex flex-wrap items-center gap-3" data-testid="shop-category-add-form">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}
        >
          <Tag className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="New category name (e.g. Cameras)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="shop-category-new-name"
          />
        </div>
        <button type="submit" className="ax-btn ax-btn-gold" disabled={adding || !newName.trim()} data-testid="shop-category-add-btn">
          <Plus className="w-4 h-4" /> Add category
        </button>
      </form>

      <div className="ax-glass overflow-hidden" data-testid="shop-categories-table-wrap">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Slug</th>
                <th>Products</th>
                <th style={{ width: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && categories.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    No categories yet.
                  </td>
                </tr>
              )}
              {!loading &&
                categories.map((c) => (
                  <tr key={c.id} data-testid={`shop-category-row-${c.id}`}>
                    <td>
                      {editingId === c.id ? (
                        <input
                          autoFocus
                          className="ax-input"
                          style={{ maxWidth: 240 }}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && saveRename(c)}
                          data-testid={`shop-category-edit-input-${c.id}`}
                        />
                      ) : (
                        <span className="text-sm font-medium">{c.name}</span>
                      )}
                    </td>
                    <td className="text-xs ax-mono" style={{ color: "var(--ax-text-muted)" }}>
                      /{c.slug}
                    </td>
                    <td className="text-sm">{c.product_count ?? 0}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        {editingId === c.id ? (
                          <button type="button" className="ax-btn ax-btn-ghost px-2" onClick={() => saveRename(c)} data-testid={`shop-category-save-${c.id}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        ) : (
                          <button type="button" className="ax-btn ax-btn-ghost px-2" onClick={() => startRename(c)} data-testid={`shop-category-rename-${c.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {c.name !== "Uncategorized" && (
                          <button
                            type="button"
                            className="ax-btn ax-btn-ghost ax-btn-danger px-2"
                            onClick={() => deleteCategory(c)}
                            data-testid={`shop-category-delete-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ShopOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/shop-orders");
      setOrders(r.data?.orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const updateStatus = async (order, status) => {
    try {
      await adminApi.put(`/shop-orders/${order.id}/status`, { status });
      toast.success("Order status updated");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (!q) return true;
      return o.order_number?.toLowerCase().includes(q) || o.customer_email?.toLowerCase().includes(q);
    });
  }, [orders, search, statusFilter]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[240px]"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--ax-border)" }}
        >
          <Search className="w-4 h-4" style={{ color: "var(--ax-text-muted)" }} />
          <input
            className="bg-transparent flex-1 outline-none text-sm"
            placeholder="Search order # or customer email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="shop-orders-search"
          />
        </div>
        <select
          className="ax-select"
          style={{ width: 160 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="shop-orders-status-filter"
        >
          <option value="all">All statuses</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="ax-glass overflow-hidden" data-testid="shop-orders-table-wrap">
        <div className="ax-scroll overflow-x-auto">
          <table className="ax-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Order #</th>
                <th>Customer</th>
                <th>Items</th>
                <th>Total</th>
                <th>Status</th>
                <th>Placed</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center py-14 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                    <PackageX className="w-6 h-6 mx-auto mb-2 opacity-60" />
                    {orders.length === 0 ? "No orders yet." : "No orders match your search."}
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((o) => (
                  <React.Fragment key={o.id}>
                    <tr data-testid={`shop-order-row-${o.id}`}>
                      <td>
                        <button
                          type="button"
                          className="ax-btn ax-btn-ghost px-2"
                          onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                          data-testid={`shop-order-expand-${o.id}`}
                        >
                          {expandedId === o.id ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                      <td className="text-sm ax-mono">{o.order_number}</td>
                      <td className="text-sm">{o.customer_email}</td>
                      <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
                        {o.items?.map((it) => `${it.name} ×${it.quantity}`).join(", ")}
                      </td>
                      <td className="text-sm ax-mono" style={{ color: "var(--ax-gold)" }}>
                        {formatINR(o.total)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`ax-chip ${STATUS_CHIP[o.status] || ""}`}>{o.status}</span>
                          <select
                            className="ax-select"
                            style={{ width: 120, padding: "6px 10px" }}
                            value={o.status}
                            onChange={(e) => updateStatus(o, e.target.value)}
                            data-testid={`shop-order-status-${o.id}`}
                          >
                            {ORDER_STATUSES.map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="text-xs" style={{ color: "var(--ax-text-muted)" }}>
                        {shortDate(o.created_at)}
                      </td>
                    </tr>
                    {expandedId === o.id && (
                      <tr>
                        <td colSpan={7} className="p-0">
                          <OrderDetail order={o} onSaved={refresh} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function OrderDetail({ order, onSaved }) {
  const [shippingFee, setShippingFee] = useState(order.shipping_fee);
  const [tracking, setTracking] = useState(order.tracking_number || "");
  const [saving, setSaving] = useState(false);
  const addr = order.shipping_address || {};

  const saveShipping = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/shop-orders/${order.id}/shipping`, { shipping_fee: parseNumber(shippingFee) });
      toast.success("Shipping fee updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const saveTracking = async () => {
    setSaving(true);
    try {
      await adminApi.put(`/shop-orders/${order.id}/status`, { status: order.status, tracking_number: tracking });
      toast.success("Tracking number saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-6"
      style={{ background: "rgba(255,255,255,0.015)", borderTop: "1px solid var(--ax-border)" }}
      data-testid={`shop-order-detail-${order.id}`}
    >
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <p className="ax-label mb-2">Shipping address</p>
          <p className="text-sm">{addr.full_name}</p>
          <p className="text-sm" style={{ color: "var(--ax-text-dim)" }}>
            {addr.phone}
          </p>
          <p className="text-sm mt-1" style={{ color: "var(--ax-text-dim)" }}>
            {addr.line1}
            {addr.line2 ? `, ${addr.line2}` : ""}, {addr.city}, {addr.state} {addr.pincode}
          </p>
          {order.payu_txnid && (
            <p className="text-xs ax-mono mt-3" style={{ color: "var(--ax-text-muted)" }}>
              PayU txn: {order.payu_txnid}
            </p>
          )}
        </div>
        <div>
          <p className="ax-label mb-2">Items</p>
          <div className="space-y-1.5">
            {order.items?.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span style={{ color: "var(--ax-text-dim)" }}>
                  {it.name} × {it.quantity}
                </span>
                <span className="ax-mono">{formatINR(it.unit_price * it.quantity)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm pt-2" style={{ borderTop: "1px solid var(--ax-border)" }}>
              <span style={{ color: "var(--ax-text-dim)" }}>Subtotal</span>
              <span className="ax-mono">{formatINR(order.subtotal)}</span>
            </div>
          </div>
        </div>
        <div>
          <p className="ax-label mb-2">Shipping fee (₹)</p>
          <div className="flex items-center gap-2">
            <input
              className="ax-input ax-mono"
              style={{ width: 110 }}
              value={shippingFee}
              onChange={(e) => setShippingFee(e.target.value)}
              data-testid={`shop-order-shipping-fee-${order.id}`}
            />
            <button
              type="button"
              className="ax-btn ax-btn-ghost"
              onClick={saveShipping}
              disabled={saving}
              data-testid={`shop-order-save-shipping-${order.id}`}
            >
              Save
            </button>
          </div>
          <p className="ax-label mt-5 mb-2">Tracking number</p>
          <div className="flex items-center gap-2">
            <input
              className="ax-input"
              placeholder="e.g. Delhivery AWB number"
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              data-testid={`shop-order-tracking-${order.id}`}
            />
            <button
              type="button"
              className="ax-btn ax-btn-ghost"
              onClick={saveTracking}
              disabled={saving}
              data-testid={`shop-order-save-tracking-${order.id}`}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ShopSettingsTab({ categories = [] }) {
  const [form, setForm] = useState({ shipping_fee: 99, free_shipping_threshold: 1999, category_shipping_fees: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi
      .get("/shop-settings")
      .then((r) => setForm({ category_shipping_fees: {}, ...r.data }))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const setCategoryFee = (name, value) => {
    setForm((f) => {
      const next = { ...(f.category_shipping_fees || {}) };
      if (value === "") delete next[name];
      else next[name] = value;
      return { ...f, category_shipping_fees: next };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanFees = {};
      Object.entries(form.category_shipping_fees || {}).forEach(([k, v]) => {
        if (v !== "" && v != null) cleanFees[k] = parseNumber(v);
      });
      const r = await adminApi.put("/shop-settings", {
        shipping_fee: parseNumber(form.shipping_fee),
        free_shipping_threshold: parseNumber(form.free_shipping_threshold),
        category_shipping_fees: cleanFees,
      });
      setForm({ category_shipping_fees: {}, ...r.data });
      toast.success("Shipping settings saved");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm py-10 text-center" style={{ color: "var(--ax-text-muted)" }}>Loading…</div>;
  }

  return (
    <form onSubmit={save} className="ax-glass p-7 max-w-lg" data-testid="shop-settings-form">
      <div className="flex items-center gap-2 mb-6">
        <Truck className="w-4 h-4" style={{ color: "var(--ax-gold)" }} />
        <h3 className="ax-serif text-2xl">Shipping</h3>
      </div>
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="ax-label">Default flat shipping fee (₹)</label>
          <input
            className="ax-input ax-mono"
            value={form.shipping_fee}
            onChange={(e) => setForm((f) => ({ ...f, shipping_fee: e.target.value }))}
            data-testid="shop-settings-fee"
          />
        </div>
        <div>
          <label className="ax-label">Free shipping above (₹)</label>
          <input
            className="ax-input ax-mono"
            value={form.free_shipping_threshold}
            onChange={(e) => setForm((f) => ({ ...f, free_shipping_threshold: e.target.value }))}
            data-testid="shop-settings-threshold"
          />
        </div>
      </div>
      <p className="text-xs mt-4" style={{ color: "var(--ax-text-muted)" }}>
        Orders with a subtotal at or above the free-shipping threshold ship free. Otherwise the default fee applies.
      </p>

      {categories.length > 0 && (
        <div className="mt-7 pt-6" style={{ borderTop: "1px solid var(--ax-border)" }}>
          <label className="ax-label">Per-category shipping fee overrides (₹)</label>
          <p className="text-xs mb-3" style={{ color: "var(--ax-text-muted)" }}>
            Leave blank to use the default fee. If a cart mixes categories, the highest applicable fee applies.
          </p>
          <div className="space-y-2">
            {categories.map((c) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="text-sm flex-1" style={{ color: "var(--ax-text-dim)" }}>{c.name}</span>
                <input
                  className="ax-input ax-mono w-32"
                  placeholder={`Default (₹${form.shipping_fee})`}
                  value={form.category_shipping_fees?.[c.name] ?? ""}
                  onChange={(e) => setCategoryFee(c.name, e.target.value)}
                  data-testid={`shop-settings-category-fee-${c.id}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <button type="submit" className="ax-btn ax-btn-gold" disabled={saving} data-testid="shop-settings-save">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}
